import { Clear, FilterAlt } from '@mui/icons-material'
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import {
  type ExcelConversionFlow,
  ExcelConversionLogStatusEnum,
  type GetExcelConversionLogsRequest,
  type PlaceNode,
} from '@ulysses-inc/harami_api_client'
import { format } from 'date-fns' // eslint-disable-line import/no-duplicates
import { ja } from 'date-fns/locale' // eslint-disable-line import/no-duplicates
import React, { useEffect, useState } from 'react'
import { getStatusName } from 'src/constants/excelConversionLogStatus'
import { usePlaceLoadingStatus } from 'src/exShared/hooks/usePlaceLoadingStatus'
import {
  initializePlaceFilter,
  splitAccessiblePlaceNode,
} from 'src/exShared/libraries/filterDropDown/place'
import date from 'src/exShared/util/date'
import { mergedPlaceNodeIds } from 'src/exShared/util/place/placeNode'
import { splitLoginPlaceUUIDs } from 'src/exShared/util/place/splitLoginPlace'
import { useListFilter } from 'src/hooks/filter/useListFilter'
import { GetExcelConversionLogsFilter } from 'src/state/ducks/excelConversionLogs/types'

interface OwnProps {
  flows: ExcelConversionFlow[]
  places: PlaceNode[]
  placeNodes: PlaceNode[]
  getExcelConversionLogs: (filter?: GetExcelConversionLogsFilter) => void
  isLoadingPlace?: boolean
  logGetTrigger?: boolean
  request?: GetExcelConversionLogsRequest
}

const statusOptions = [
  {
    label: getStatusName(ExcelConversionLogStatusEnum.SUCCESS),
    value: ExcelConversionLogStatusEnum.SUCCESS,
  },
  {
    label: getStatusName(ExcelConversionLogStatusEnum.FAILED),
    value: ExcelConversionLogStatusEnum.FAILED,
  },
]

const ExcelConversionLogFilterDropDown: React.FC<OwnProps> = (
  props: OwnProps,
) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = anchorEl !== null
  const [flowIds, setFlowIds] = useState<string[]>([])
  const [status, setStatus] = useState<number | undefined>(undefined)
  const [placeGroupNodeIds, setPlaceGroupNodeIds] = useState<string[]>([])
  const [placeNodeIds, setPlaceNodeIds] = useState<string[]>([])
  const [from, setFrom] = useState<string | undefined>(undefined)
  const [through, setThrough] = useState<string | undefined>(undefined)
  const { filters, setListFilter } = useListFilter()

  const { loginPlaceGroupIds, loginPlaceIds } = splitLoginPlaceUUIDs()
  const placeLoadingStatus = usePlaceLoadingStatus()

  useEffect(() => {
    if (!props.logGetTrigger) {
      return
    }
    // placeNodes のロード完了を待つ（useEffect が再発火する）
    if (placeLoadingStatus !== 'finish') {
      return
    }
    if (!filters?.excelConversionLogs) {
      const placeFilter = initializePlaceFilter(props.placeNodes)
      if (placeFilter === undefined || placeFilter.$in.length === 0) {
        props.getExcelConversionLogs()
        return
      }
      const filter = { placeNodeUUIDs: placeFilter }
      props.getExcelConversionLogs(filter)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
      setPlaceNodeIds(loginPlaceIds)
      setPlaceGroupNodeIds(loginPlaceGroupIds)
      return
    }
    const logFilter = filters.excelConversionLogs?.filter
    let filterPlaceNodeIds: string[] = []
    let filterPlaceGroupNodeIds: string[] = []
    if (
      (logFilter?.placeNodeIds ?? []).length > 0 ||
      (logFilter?.placeGroupNodeIds ?? []).length > 0
    ) {
      filterPlaceNodeIds = logFilter?.placeNodeIds ?? []
      filterPlaceGroupNodeIds = logFilter?.placeGroupNodeIds ?? []
    } else {
      filterPlaceNodeIds = loginPlaceIds
      filterPlaceGroupNodeIds = loginPlaceGroupIds
    }
    setPlaceNodeIds(filterPlaceNodeIds)
    setPlaceGroupNodeIds(filterPlaceGroupNodeIds)
    setFlowIds(logFilter?.flowUUIDs ?? [])
    setStatus(logFilter?.status)
    setFrom(logFilter?.logDate?.from)
    setThrough(logFilter?.logDate?.through)
    // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
    const filter = getFilter(
      logFilter?.flowUUIDs,
      logFilter?.status,
      filterPlaceNodeIds,
      filterPlaceGroupNodeIds,
      logFilter?.logDate?.from,
      logFilter?.logDate?.through,
    )
    props.getExcelConversionLogs(filter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.placeNodes.length, placeLoadingStatus])

  const accessiblePlaceNode = splitAccessiblePlaceNode(
    props.placeNodes,
    props.places,
  )

  const isFilterOn = () => {
    return (
      flowIds.length > 0 ||
      from !== undefined ||
      placeGroupNodeIds.length > 0 ||
      placeNodeIds.length > 0 ||
      through !== undefined ||
      status !== undefined
    )
  }

  const clearFilter = () => {
    const filter = getFilter([], undefined, loginPlaceIds, loginPlaceGroupIds)
    props.getExcelConversionLogs(filter)
    setPlaceNodeIds([])
    setPlaceGroupNodeIds([])
    setFlowIds([])
    setStatus(undefined)
    setFrom(undefined)
    setThrough(undefined)
    saveFilter()
  }

  const saveFilter = (
    flowIds?: string[],
    status?: number,
    placeNodeIds?: string[],
    placeGroupNodeIds?: string[],
    from?: string,
    through?: string,
  ) => {
    setListFilter({
      ...(filters ?? {}),
      excelConversionLogs: {
        pagination: props.request,
        filter: {
          flowUUIDs: flowIds,
          placeNodeIds: placeNodeIds,
          placeGroupNodeIds: placeGroupNodeIds,
          logDate: { from, through },
          status: status,
        },
      },
    })
  }

  const getFilter = (
    flowIds?: string[],
    status?: number,
    placeNodeIds?: string[],
    placeGroupNodeIds?: string[],
    from?: string,
    through?: string,
  ) => {
    const allPlaceNodeIds = mergedPlaceNodeIds(
      props.placeNodes,
      placeNodeIds ?? [],
      placeGroupNodeIds ?? [],
    )
    const flowIdCondition =
      flowIds && flowIds.length > 0 ? { $in: flowIds } : undefined
    // throughは画面表示の日付＋１日する
    const logDateCondition =
      from && through
        ? {
            $gte: date.parseYYYYMMDD(from),
            $lte: date.nextDay(date.parseYYYYMMDD(through)),
          }
        : undefined
    const placeNodeIdCondition =
      allPlaceNodeIds && allPlaceNodeIds.length > 0
        ? { $in: allPlaceNodeIds }
        : undefined
    const statusCondition = status !== undefined ? { $in: [status] } : undefined
    saveFilter(flowIds, status, placeNodeIds, placeGroupNodeIds, from, through)
    return {
      flowUUIDs: flowIdCondition,
      logDate: logDateCondition,
      placeNodeUUIDs: placeNodeIdCondition,
      status: statusCondition,
    }
  }

  const getEffectivePlaceNodeIds = () =>
    placeNodeIds.length > 0 || placeGroupNodeIds.length > 0
      ? placeNodeIds
      : loginPlaceIds

  const getEffectivePlaceGroupNodeIds = () =>
    placeNodeIds.length > 0 || placeGroupNodeIds.length > 0
      ? placeGroupNodeIds
      : loginPlaceGroupIds

  return (
    <Box>
      <Button
        color={isFilterOn() ? 'filtered' : 'secondary'}
        variant="outlined"
        startIcon={<FilterAlt />}
        disabled={props.isLoadingPlace}
        onClick={({ currentTarget }) => setAnchorEl(currentTarget)}
      >
        絞り込み: {isFilterOn() ? 'ON' : 'OFF'}
      </Button>
      <Popover
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { padding: 2, width: '100%', maxWidth: 420 },
          },
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="flex-end">
            <Button onClick={clearFilter}>クリア</Button>
          </Stack>
          <Stack>
            <Typography variant="subtitle1">変換フロー名</Typography>
            <Autocomplete<ExcelConversionFlow, true, false, false>
              multiple
              size="small"
              disableCloseOnSelect
              options={props.flows}
              getOptionLabel={option => option.name ?? ''}
              value={flowIds
                .map(uuid => props.flows.find(flow => flow.uuid === uuid))
                .filter(
                  (flow): flow is ExcelConversionFlow => flow !== undefined,
                )}
              onChange={(_, newValue) => {
                const newFlowIds = newValue.map(flow => flow.uuid ?? '')
                setFlowIds(newFlowIds)
                const filter = getFilter(
                  newFlowIds,
                  status,
                  getEffectivePlaceNodeIds(),
                  getEffectivePlaceGroupNodeIds(),
                  from,
                  through,
                )
                props.getExcelConversionLogs(filter)
              }}
              isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
              slotProps={{ chip: { variant: 'pale', size: 'small' } }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                    <Checkbox size="small" checked={selected} />
                    {option.name}
                  </Stack>
                </li>
              )}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField {...params} />
              )}
            />
          </Stack>
          <Stack>
            <Typography variant="subtitle1">実行結果</Typography>
            <Select
              size="small"
              fullWidth
              value={status ?? ''}
              onChange={(e: SelectChangeEvent<number | ''>) => {
                const val =
                  e.target.value === '' ? undefined : Number(e.target.value)
                setStatus(val)
                const filter = getFilter(
                  flowIds,
                  val,
                  getEffectivePlaceNodeIds(),
                  getEffectivePlaceGroupNodeIds(),
                  from,
                  through,
                )
                props.getExcelConversionLogs(filter)
              }}
              displayEmpty
              endAdornment={
                status !== undefined ? (
                  <InputAdornment position="end" sx={{ mr: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setStatus(undefined)
                        const filter = getFilter(
                          flowIds,
                          undefined,
                          getEffectivePlaceNodeIds(),
                          getEffectivePlaceGroupNodeIds(),
                          from,
                          through,
                        )
                        props.getExcelConversionLogs(filter)
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }
            >
              {statusOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>
          <Stack>
            <Typography variant="subtitle1">現場グループ名</Typography>
            <Autocomplete<PlaceNode, true, false, false>
              multiple
              size="small"
              disableCloseOnSelect
              options={accessiblePlaceNode.accessiblePlaceGroups}
              getOptionLabel={option => option.placeGroup?.name ?? '名称未設定'}
              value={placeGroupNodeIds
                .map(uuid =>
                  accessiblePlaceNode.accessiblePlaceGroups.find(
                    p => p.uuid === uuid,
                  ),
                )
                .filter((p): p is PlaceNode => p !== undefined)}
              onChange={(_, newValue) => {
                const newPlaceGroupNodeIds = newValue.map(p => p.uuid)
                setPlaceGroupNodeIds(newPlaceGroupNodeIds)
                const filterPlaceNodeIds =
                  placeNodeIds.length > 0 || newPlaceGroupNodeIds.length > 0
                    ? placeNodeIds
                    : loginPlaceIds
                const filterPlaceGroupNodeIds =
                  placeNodeIds.length > 0 || newPlaceGroupNodeIds.length > 0
                    ? newPlaceGroupNodeIds
                    : loginPlaceGroupIds
                const filter = getFilter(
                  flowIds,
                  status,
                  filterPlaceNodeIds,
                  filterPlaceGroupNodeIds,
                  from,
                  through,
                )
                props.getExcelConversionLogs(filter)
              }}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              slotProps={{ chip: { variant: 'pale', size: 'small' } }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                    <Checkbox size="small" checked={selected} />
                    {option.placeGroup?.name}
                  </Stack>
                </li>
              )}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField {...params} />
              )}
            />
          </Stack>
          <Stack>
            <Typography variant="subtitle1">現場名</Typography>
            <Autocomplete<PlaceNode, true, false, false>
              multiple
              size="small"
              disableCloseOnSelect
              options={accessiblePlaceNode.accessiblePlaces}
              getOptionLabel={option => option.place?.name ?? '名称未設定'}
              value={placeNodeIds
                .map(uuid =>
                  accessiblePlaceNode.accessiblePlaces.find(
                    p => p.uuid === uuid,
                  ),
                )
                .filter((p): p is PlaceNode => p !== undefined)}
              onChange={(_, newValue) => {
                const newPlaceNodeIds = newValue.map(p => p.uuid)
                setPlaceNodeIds(newPlaceNodeIds)
                const filterPlaceNodeIds =
                  newPlaceNodeIds.length > 0 || placeGroupNodeIds.length > 0
                    ? newPlaceNodeIds
                    : loginPlaceIds
                const filterPlaceGroupNodeIds =
                  newPlaceNodeIds.length > 0 || placeGroupNodeIds.length > 0
                    ? placeGroupNodeIds
                    : loginPlaceGroupIds
                const filter = getFilter(
                  flowIds,
                  status,
                  filterPlaceNodeIds,
                  filterPlaceGroupNodeIds,
                  from,
                  through,
                )
                props.getExcelConversionLogs(filter)
              }}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              slotProps={{ chip: { variant: 'pale', size: 'small' } }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                    <Checkbox size="small" checked={selected} />
                    {option.place?.name}
                  </Stack>
                </li>
              )}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField {...params} />
              )}
            />
          </Stack>
          <Stack>
            <Typography variant="subtitle1">実施日</Typography>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ja}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <DatePicker
                  format="yyyy/MM/dd"
                  value={from ? new Date(from) : null}
                  onChange={(newDate: Date | null) => {
                    const newFrom = newDate
                      ? format(newDate, 'yyyy-MM-dd')
                      : undefined
                    setFrom(newFrom)
                    const filter = getFilter(
                      flowIds,
                      status,
                      getEffectivePlaceNodeIds(),
                      getEffectivePlaceGroupNodeIds(),
                      newFrom,
                      through,
                    )
                    props.getExcelConversionLogs(filter)
                  }}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <Typography>〜</Typography>
                <DatePicker
                  format="yyyy/MM/dd"
                  value={through ? new Date(through) : null}
                  onChange={(newDate: Date | null) => {
                    const newThrough = newDate
                      ? format(newDate, 'yyyy-MM-dd')
                      : undefined
                    setThrough(newThrough)
                    const filter = getFilter(
                      flowIds,
                      status,
                      getEffectivePlaceNodeIds(),
                      getEffectivePlaceGroupNodeIds(),
                      from,
                      newThrough,
                    )
                    props.getExcelConversionLogs(filter)
                  }}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </Stack>
            </LocalizationProvider>
          </Stack>
        </Stack>
      </Popover>
    </Box>
  )
}

export default ExcelConversionLogFilterDropDown
